import * as BookTypes from './Book.types'
import { UserServiceActionTypes } from '../User'
import { flatten, update } from 'ramda'

export const bookServiceInitialState: BookTypes.BookServiceInitialState = {
  entities: [],
  lastUpdated: null,
  bookStarted: null,
  progressEntities: [],
  currentBookId: null,
  currentBookMode: null,
  placementTestEntity: null,
  placementTestScoreEntity: null
}

export const bookReducer = (
  state: BookTypes.BookServiceInitialState = bookServiceInitialState,
  action: BookTypes.BookServiceActions
): BookTypes.BookServiceInitialState => {
  switch (action.type) {
    case UserServiceActionTypes.LOGOUT: {
      return bookServiceInitialState
    }

    case BookTypes.BookServiceActionTypes.SetCurrentBookId: {
      return {
        ...state,
        currentBookId: action.bookId,
        currentBookMode: action.bookMode
      }
    }

    case BookTypes.BookServiceActionTypes.SetBookStarted: {
      return {
        ...state,
        bookStarted: action.timestamp
      }
    }

    case BookTypes.BookServiceActionTypes.AsyncGetAll: {
      return {
        ...state,
        lastUpdated: new Date().toISOString(),
        entities: action.bookEntities
      }
    }

    case BookTypes.BookServiceActionTypes.AsyncGetBooksProgress: {
      // We filter existing progresses and leave only those that are not present in the incoming progresses:
      const filteredExistingProgresses = state.progressEntities.filter(
        (current) => !action.progressEntities.find((incoming) => current._id === incoming._id)
      )

      return {
        ...state,
        progressEntities: [...filteredExistingProgresses, ...action.progressEntities]
      }
    }

    case BookTypes.BookServiceActionTypes.AsyncCreateProgressEntity: {
      const filteredProgresses = state.progressEntities.filter(
        (p) => action.progressEntity && p._id !== action.progressEntity._id
      )

      return {
        ...state,
        progressEntities: [...filteredProgresses, action.progressEntity]
      }
    }

    case BookTypes.BookServiceActionTypes.AsyncUpdateProgressEntity: {
      let updatedProgresses = [...state.progressEntities]
      const updatedEntityIndex = state.progressEntities.findIndex(
        ({ _id }) => action.progressEntity && _id === action.progressEntity._id
      )

      const journeyPageBookProgressIndex = state.progressEntities.findIndex(
        ({ bookId, isJourneyPage }) => action.progressEntity.bookId === bookId && isJourneyPage
      )

      if (updatedEntityIndex !== -1 && !action.progressEntity.isJourneyPage) {
        updatedProgresses = update(updatedEntityIndex, action.progressEntity, updatedProgresses)
      }

      if (journeyPageBookProgressIndex !== -1 && action.progressEntity.isJourneyPage) {
        updatedProgresses = update(
          journeyPageBookProgressIndex,
          { ...updatedProgresses[journeyPageBookProgressIndex], isFavorite: action.progressEntity.isFavorite },
          updatedProgresses
        )
      }

      return {
        ...state,
        progressEntities: updatedProgresses
      }
    }

    case BookTypes.BookServiceActionTypes.AsyncGetRecommendedBook: {
      const updatedBooks: BookTypes.Book[] = flatten(
        state.entities.map((book) => {
          if (action.book && book._id === action.book._id) {
            if (book.bookType === BookTypes.BookType.Mix) {
              return [
                {
                  ...book,
                  bookType: BookTypes.BookType.Reading,
                  recommended: action.book.bookType === BookTypes.BookType.Reading
                },
                {
                  ...book,
                  bookType: BookTypes.BookType.Listening,
                  recommended: action.book.bookType === BookTypes.BookType.Listening
                }
              ]
            } else {
              return action.book
            }
          } else {
            return {
              ...book,
              recommended: false
            }
          }
        })
      )

      return {
        ...state,
        entities: updatedBooks
      }
    }

    case BookTypes.BookServiceActionTypes.AsyncGetPlacementTest: {
      return {
        ...state,
        placementTestEntity: action.placementTestEntity
      }
    }

    case BookTypes.BookServiceActionTypes.AsyncCreatePlacementTestScore: {
      return {
        ...state,
        placementTestScoreEntity: action.placementTestScoreEntity
      }
    }

    case BookTypes.BookServiceActionTypes.AsyncUpdatePlacementTestScore: {
      return {
        ...state,
        placementTestScoreEntity: action.placementTestScoreEntity
      }
    }

    default:
      return state
  }
}
